/**
 * purchaseInvoiceFormTypes.ts — typ formularza faktury zakupu + jego pusty stan.
 *
 * Relokowane z dawnego `PurchaseInvoiceDataTab.tsx` (usunięty — silnik formatek,
 * unifikacja-formatek Krok 2: `PurchaseInvoiceDetail` renderuje teraz przez
 * `RecursiveFormRenderer` / `purchaseInvoiceEngineConfig`, a nie przez ten
 * komponent). Typ i stała nadal są potrzebne — `PurchaseInvoiceDetail`,
 * `invoiceEngineConfig.ts` i testy budują na nich stan formularza.
 */

export interface PurchaseInvoiceFormData {
  vendorId: string;
  systemNumber: string | null;
  invoiceNumber: string;
  ksefNumber: string | null;
  // Ewidencja KSeF/JPK (0159). Ostatnia trójka jest tylko do odczytu — wypełnia ją import
  // z KSeF, trasy zapisu jej nie przyjmują, więc NIE trafia do payloadu zapisu.
  ksefIssueDate: string | null;
  purchaseEvidenceMarker: string | null;
  jpkDocumentMarker: string | null;
  goodsImport: boolean;
  ksefIssueMode: string | null;
  documentReceiptChannel: string | null;
  ksefSubmittedAt: string | null;
  ksefAvailableAt: string | null;
  ksefSourceIp: string | null;
  documentType: string;
  status: string;
  urgency: string;
  correctedInvoiceId: string | null;
  issueDate: string;
  receivedDate: string;
  saleDate: string | null;
  paymentDueDate: string;
  vendorTaxIdCached: string | null;
  vendorNameCached: string | null;
  currencyCode: string;
  exchangeRate: string | null;
  amountNet: string;
  amountVat: string | null;
  amountGross: string;
  paymentMethod: string | null;
  paidAt: string | null;
  vendorBankAccountId: string | null;
  paymentNotes: string | null;
  description: string | null;
  costCenterDefault: string | null;
  rejectionReason: string | null;
  notes: string | null;
  ownerUserId: string | null;
  /** Kategoria dokumentu (oś `record_categories`, kartoteka_key='documents'). Jedna, opcjonalna. */
  categoryId: string | null;
  // T21 (finding CRITICAL — sekcja „Źródło" bez renderera). Read-only w UI:
  // PATCH_BODY_SCHEMA (apps/api/app/api/purchase-invoices/[id]/route.ts) NIE
  // przyjmuje tych pól — `source` ustawia backend przy tworzeniu ('manual') lub
  // imporcie ERP/KSeF ('symfonia'/'ksef'), `externalId`/`externalSource` to
  // identyfikatory idempotency importu.
  source: string;
  externalId: string | null;
  externalSource: string | null;
  documentTypeId?: string | null;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const EMPTY_PURCHASE_INVOICE_FORM: PurchaseInvoiceFormData = {
  vendorId: '',
  systemNumber: null,
  invoiceNumber: '',
  ksefNumber: null,
  ksefIssueDate: null,
  // Domyślnie „faktura wystawiona poza KSeF" — dziś to najczęstszy przypadek faktury
  // wpisywanej ręcznie, a znacznik jest obowiązkowy od 1.02.2026.
  purchaseEvidenceMarker: 'BFK',
  jpkDocumentMarker: null,
  goodsImport: false,
  ksefIssueMode: null,
  documentReceiptChannel: null,
  ksefSubmittedAt: null,
  ksefAvailableAt: null,
  ksefSourceIp: null,
  documentType: 'VAT',
  status: 'draft',
  urgency: 'normal',
  correctedInvoiceId: null,
  issueDate: today(),
  receivedDate: today(),
  saleDate: null,
  paymentDueDate: addDays(today(), 14),
  vendorTaxIdCached: null,
  vendorNameCached: null,
  currencyCode: 'PLN',
  exchangeRate: null,
  amountNet: '',
  amountVat: null,
  amountGross: '',
  paymentMethod: 'transfer',
  paidAt: null,
  vendorBankAccountId: null,
  paymentNotes: null,
  description: null,
  costCenterDefault: null,
  rejectionReason: null,
  notes: null,
  ownerUserId: null,
  categoryId: null,
  source: 'manual',
  externalId: null,
  externalSource: null,
  documentTypeId: null,
};
